import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Progress } from '@/components/ui/progress';
import api, { BACKEND_URL } from '@/lib/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  ArrowLeft, Download, FileText, Calendar, Package, Clock,
  Shield, CheckCircle2, Camera, ChevronDown,
  ChevronRight, FolderOpen, Send, FileCheck,
  Maximize2, X
} from 'lucide-react';

const statusConfig = {
  'On Track': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'At Risk': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  'Delayed': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  'Completed': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

const COMPLIANCE_SECTIONS = ['Fabric', 'Cutting', 'Stitching', 'Quality', 'Finishing'];

export default function BuyerOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCompliance, setExpandedCompliance] = useState({});
  const [imageExpanded, setImageExpanded] = useState(false);

  const loadOrder = useCallback(async () => {
    try { const res = await api.get(`/orders/${id}`); setOrder(res.data); } catch (e) { console.error(e); }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  const downloadFile = (fileId) => {
    const token = localStorage.getItem('gh_token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    window.open(`${baseUrl}/api/files/${fileId}/download?auth=${token}`, '_blank');
  };

  const toggleCompliance = (section) => setExpandedCompliance(prev => ({ ...prev, [section]: !prev[section] }));

  if (loading) return <DashboardLayout><div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;
  if (!order) return <DashboardLayout><div className="text-center py-20"><p className="text-sm text-[#A1A1AA]">Order not found</p></div></DashboardLayout>;

  const sc = statusConfig[order.status] || statusConfig['On Track'];
  const files = order.files || [];
  const samFiles = files.filter(f => f.category === 'sam_study');
  const complianceFiles = files.filter(f => f.category === 'compliance');
  const pilotFiles = files.filter(f => f.category === 'pilot');
  const pilot = order.pilot || {};

  const complianceBySub = {};
  COMPLIANCE_SECTIONS.forEach(s => { complianceBySub[s] = []; });
  complianceFiles.forEach(f => { const sub = f.stage || 'General'; if (complianceBySub[sub]) complianceBySub[sub].push(f); });

  return (
    <DashboardLayout>
      <div data-testid="buyer-order-detail" className="space-y-6 max-w-4xl">
        <button onClick={() => navigate('/dashboard/buyer')} className="inline-flex items-center gap-2 text-sm text-[#52525B] hover:text-[#0A0A0A] transition-colors" data-testid="back-to-orders-btn">
          <ArrowLeft className="w-4 h-4" /> Back to orders
        </button>

        {/* Order Image */}
        <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden" data-testid="buyer-order-image-section">
          {order.order_image ? (
            <div className="relative h-48 sm:h-64 lg:h-72 bg-[#F4F4F5] flex items-center justify-center">
              <img
                src={`${BACKEND_URL}/api/orders/${id}/image/serve?auth=${localStorage.getItem('gh_token')}`}
                alt={order.style_name}
                className="max-w-full max-h-full object-contain cursor-pointer transition-transform hover:scale-[1.02]"
                onClick={() => setImageExpanded(true)}
                data-testid="buyer-order-image"
              />
              <button onClick={() => setImageExpanded(true)} className="absolute top-3 right-3 p-1.5 rounded-md bg-white/90 backdrop-blur border border-black/[0.08] hover:bg-white transition-colors" data-testid="buyer-expand-image-btn">
                <Maximize2 className="w-3.5 h-3.5 text-[#52525B]" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-40 bg-[#FAFAF8]"><Camera className="w-8 h-8 text-[#D4D4D8] mb-2" /><p className="text-xs text-[#A1A1AA]">No image uploaded</p></div>
          )}
        </div>

        {/* Image Lightbox */}
        {imageExpanded && order.order_image && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setImageExpanded(false)} data-testid="buyer-image-lightbox">
            <button onClick={() => setImageExpanded(false)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-testid="buyer-close-lightbox-btn">
              <X className="w-6 h-6 text-white" />
            </button>
            <img
              src={`${BACKEND_URL}/api/orders/${id}/image/serve?auth=${localStorage.getItem('gh_token')}`}
              alt={order.style_name}
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="buyer-lightbox-image"
            />
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-md border border-black/[0.06] p-6 lg:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <p className="text-xs font-semibold text-[#A1A1AA] tracking-wide">{order.order_id}</p>
                <div className={`status-badge ${sc.bg} ${sc.text}`}><div className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />{order.status}</div>
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#0A0A0A] mt-2 tracking-tight">{order.style_name}</h1>
            </div>
            <div className="text-right"><p className="font-heading text-3xl font-semibold text-[#0A0A0A]">{order.progress}%</p><p className="text-xs text-[#A1A1AA] mt-0.5">Complete</p></div>
          </div>
          <Progress value={order.progress} className="h-2 bg-black/[0.06] mt-6" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-8 pt-6 border-t border-black/[0.04]">
            <BuyerInfoItem icon={Package} label="Quantity" value={`${order.quantity?.toLocaleString()} pcs`} />
            <BuyerInfoItem icon={Package} label="PA NO" value={order.pa_no || '-'} />
            <BuyerInfoItem icon={Calendar} label="Delivery" value={order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : 'N/A'} />
            <BuyerInfoItem icon={FileCheck} label="File Sent" value={order.file_sent ? 'Yes' : 'No'} />
          </div>
        </div>

        {/* Pilot Documents - view only */}
        {(pilot.sent || pilot.status || pilot.remarks || pilotFiles.length > 0) && (
          <div className="bg-white rounded-md border border-black/[0.06] p-6" data-testid="buyer-pilot-section">
            <div className="flex items-center gap-2 mb-5"><Send className="w-4 h-4 text-[#C2A878]" /><h2 className="text-sm font-semibold text-[#0A0A0A]">Pilot Documents</h2></div>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div><p className="text-[#A1A1AA] font-semibold uppercase tracking-wider text-[10px]">Pilot Sent</p><p className="font-medium text-[#0A0A0A] mt-0.5">{pilot.sent ? 'Yes' : 'No'}</p></div>
              <div><p className="text-[#A1A1AA] font-semibold uppercase tracking-wider text-[10px]">Status</p><p className="font-medium text-[#0A0A0A] mt-0.5">{pilot.status || '-'}</p></div>
              <div><p className="text-[#A1A1AA] font-semibold uppercase tracking-wider text-[10px]">Remarks</p><p className="font-medium text-[#0A0A0A] mt-0.5">{pilot.remarks || '-'}</p></div>
            </div>
            {pilotFiles.length > 0 && (
              <div className="mt-4 pt-4 border-t border-black/[0.04] space-y-1.5">
                {pilotFiles.map(file => (
                  <button key={file.id} onClick={() => downloadFile(file.id)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md bg-[#FAFAF8] border border-black/[0.04] hover:border-black/[0.1] transition-all text-left" data-testid={`buyer-pilot-file-${file.id}`}>
                    <FileText className="w-4 h-4 text-[#C2A878] flex-shrink-0" />
                    <div className="flex-1 min-w-0"><p className="text-xs font-medium text-[#0A0A0A] truncate">{file.original_filename}</p><p className="text-[10px] text-[#A1A1AA]">{file.uploaded_by_name || 'Unknown'} &middot; {new Date(file.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}</p></div>
                    <Download className="w-3.5 h-3.5 text-[#A1A1AA]" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Production Insights */}
        {order.dpr_summary && (order.dpr_summary.cutting > 0 || order.dpr_summary.stitching > 0 || order.dpr_summary.finishing > 0) && (
          <div className="bg-white rounded-md border border-black/[0.06] p-6 lg:p-8" data-testid="buyer-production-insights">
            <h2 className="text-sm font-semibold text-[#0A0A0A] mb-6">Production Insights</h2>
            <div className="grid grid-cols-3 gap-6">
              <BuyerPieCard label="Cutting" done={order.dpr_summary.cutting} remaining={order.dpr_summary.cutting_remaining} color="#3B82F6" />
              <BuyerPieCard label="Stitching" done={order.dpr_summary.stitching} remaining={order.dpr_summary.stitching_remaining} color="#F59E0B" />
              <BuyerPieCard label="Finishing" done={order.dpr_summary.finishing} remaining={order.dpr_summary.finishing_remaining} color="#10B981" />
            </div>
          </div>
        )}

        {/* Compliance Documents */}
        <div className="bg-white rounded-md border border-black/[0.06] p-6" data-testid="buyer-compliance-section">
          <div className="flex items-center gap-2 mb-5"><Shield className="w-4 h-4 text-[#6B7D5C]" /><h2 className="text-sm font-semibold text-[#0A0A0A]">Compliance Documents</h2></div>
          <div className="space-y-1">
            {COMPLIANCE_SECTIONS.map((section) => {
              const sectionFiles = complianceBySub[section] || [];
              const isOpen = expandedCompliance[section];
              return (
                <div key={section} className="border border-black/[0.04] rounded-lg overflow-hidden" data-testid={`buyer-compliance-sub-${section.toLowerCase()}`}>
                  <button onClick={() => toggleCompliance(section)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FAFAF8] transition-colors text-left" data-testid={`buyer-compliance-toggle-${section.toLowerCase()}`}>
                    <div className="flex items-center gap-3"><FolderOpen className="w-4 h-4 text-[#6B7D5C]" /><span className="text-sm font-medium text-[#0A0A0A]">{section}</span><span className="text-[10px] text-[#A1A1AA] font-medium bg-[#F5F5F5] px-1.5 py-0.5 rounded">{sectionFiles.length}</span></div>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-[#A1A1AA]" /> : <ChevronRight className="w-4 h-4 text-[#A1A1AA]" />}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t border-black/[0.04] bg-[#FAFAF8]">
                      {sectionFiles.length === 0 ? <p className="text-xs text-[#A1A1AA] py-2">No documents uploaded</p> : (
                        <div className="space-y-1.5">{sectionFiles.map(file => (
                          <button key={file.id} onClick={() => downloadFile(file.id)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md bg-white border border-black/[0.04] hover:border-black/[0.1] hover:bg-[#FAFAFA] transition-all text-left" data-testid={`buyer-download-compliance-${file.id}`}>
                            <FileText className="w-4 h-4 text-[#6B7D5C] flex-shrink-0" /><div className="flex-1 min-w-0"><p className="text-xs font-medium text-[#0A0A0A] truncate">{file.original_filename}</p><p className="text-[10px] text-[#A1A1AA]">{file.uploaded_by_name || 'Unknown'} &middot; {new Date(file.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}</p></div><Download className="w-3.5 h-3.5 text-[#A1A1AA]" />
                          </button>
                        ))}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* SAM Study - hidden from buyer view */}
      </div>
    </DashboardLayout>
  );
}

function BuyerInfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3"><Icon className="w-4 h-4 text-[#A1A1AA] mt-0.5" /><div><p className="text-[11px] uppercase tracking-wide text-[#A1A1AA] font-semibold">{label}</p><p className="text-sm font-semibold text-[#0A0A0A] mt-0.5">{value}</p></div></div>
  );
}

function BuyerPieCard({ label, done, remaining, color }) {
  const total = done + remaining;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const data = total > 0 ? [{ name: 'Done', value: done }, { name: 'Remaining', value: remaining }] : [{ name: 'No Data', value: 1 }];
  const colors = total > 0 ? [color, '#F0F0ED'] : ['#E4E4E7'];
  return (
    <div className="text-center" data-testid={`buyer-dpr-pie-${label.toLowerCase()}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#71717A] mb-3">{label}</p>
      <div className="relative w-[120px] h-[120px] mx-auto">
        <ResponsiveContainer width={120} height={120}><PieChart><Pie data={data} cx="50%" cy="50%" innerRadius={36} outerRadius={54} dataKey="value" strokeWidth={0} paddingAngle={2}>{data.map((d, i) => <Cell key={d.name} fill={colors[i]} />)}</Pie><Tooltip content={({ active, payload }) => { if (!active || !payload?.length) return null; return (<div className="bg-white border border-black/[0.08] rounded-lg px-3 py-1.5 shadow-lg text-xs"><span className="font-medium">{payload[0].name}: {payload[0].value.toLocaleString()}</span></div>); }} /></PieChart></ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"><p className="font-heading text-lg font-semibold text-[#0A0A0A]">{pct}%</p></div>
      </div>
      <div className="mt-3 space-y-1">
        <div className="flex items-center justify-center gap-2"><div className="w-2 h-2 rounded-full" style={{ background: color }} /><span className="text-xs text-[#52525B]">Done: <span className="font-semibold text-[#0A0A0A]">{done.toLocaleString()}</span></span></div>
        <div className="flex items-center justify-center gap-2"><div className="w-2 h-2 rounded-full bg-[#F0F0ED]" /><span className="text-xs text-[#52525B]">Remaining: <span className="font-semibold text-[#0A0A0A]">{remaining.toLocaleString()}</span></span></div>
      </div>
    </div>
  );
}
